import {
  ActionBar,
  ActionInfo,
  Card,
  EmptyState,
  FileIcon,
  Loading,
  PageHead,
  formatDate,
  formatDateTime,
} from "@corecrm/ui";
import { Link, useParams } from "react-router-dom";
import { useDeliverable } from "../api/queries";
import type { Deliverable } from "../api/types";
import { Crumb, LoadError, ProjectLink, Status } from "../components/bits";
import { DeliverableActions } from "../components/DeliverableActions";
import { fileLink } from "../components/DeliverableFiles";
import { fileSize } from "../lib/labels";

const ACTION: Record<string, string> = {
  submit: "Sent for your approval",
  approve: "Approved",
  request_changes: "Changes requested",
  recall: "Taken back by your project team",
  remind: "Reminder sent",
};

/** One deliverable (linked from the "ready for your approval" email): files, history, and the
    approve / request-changes actions while it's with the client. */
export function DeliverablePage() {
  const { id = "" } = useParams();
  const d = useDeliverable(id);
  if (d.isPending) return <Loading />;
  if (d.error)
    return (
      <LoadError
        error={d.error}
        onRetry={() => d.refetch()}
        what="deliverable"
        back={["/projects", "Back to projects"]}
      />
    );
  return <DeliverableView deliverable={d.data} />;
}

function DeliverableView({ deliverable: d }: { deliverable: Deliverable }) {
  return (
    <>
      <Crumb to="/projects" label="Projects">
        <ProjectLink id={d.project_id} /> / {d.title}
      </Crumb>
      <PageHead
        title={d.title}
        sub={
          <>
            <Status status={d.status} />
            <span>{d.phase_name}</span>
            {d.submitted_at && <span>Submitted {formatDate(d.submitted_at)}</span>}
          </>
        }
      />
      {d.description && <p style={{ whiteSpace: "pre-line" }}>{d.description}</p>}
      {d.can_respond && (
        <section className="accept" aria-label="Your approval">
          <p>Review the files below, then approve it or tell the team what needs to change.</p>
          <div className="row">
            <DeliverableActions deliverable={d} />
          </div>
        </section>
      )}
      <Card title="Files">
        {d.files.length ? (
          <div className="item-list">
            {d.files.map((f) => (
              <div className="item" key={f.id}>
                <div className="file-name">
                  <FileIcon filename={f.filename} />
                  <div className="what">
                    <a {...fileLink(d.id, f)}>
                      <strong>{f.filename}</strong>
                    </a>
                    <span>{fileSize(f.size)}</span>
                  </div>
                </div>
                <a className="btn sm" {...fileLink(d.id, f, true)}>
                  Download
                </a>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState>No files attached.</EmptyState>
        )}
      </Card>
      <Card title="History">
        {d.history.length ? (
          <div className="item-list">
            {[...d.history].reverse().map((h, i) => (
              <div className="item" key={i}>
                <div className="what">
                  <strong>
                    {ACTION[h.action] ?? h.action}
                    {h.by_client ? " by you" : ""}
                  </strong>
                  <span>{formatDateTime(h.occurred_at)}</span>
                  {h.comment && <span>“{h.comment}”</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState>Nothing yet.</EmptyState>
        )}
      </Card>
      <p>
        <Link to={`/projects/${d.project_id}`}>Back to the project</Link>
      </p>
      {d.can_respond && (
        <ActionBar info={<ActionInfo wide label="Awaiting your approval" value={d.title} />}>
          <DeliverableActions deliverable={d} />
        </ActionBar>
      )}
    </>
  );
}
